import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, ExternalLink } from "lucide-react";

interface Avaliacao {
  autor: string;
  nota: number;
  texto: string;
  tempo: string;
  foto: string | null;
}

interface DadosGoogle {
  nome: string | null;
  nota: number | null;
  total: number | null;
  avaliacoes: Avaliacao[];
}

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/place/Atacado+%C3%93ptico+Lentes,Arma%C3%A7%C3%A3o,+Fornitura+e+Acess%C3%B3rios+%C3%B3ptico/@-19.9197793,-43.9566736,15z/data=!4m10!1m2!2m1!1sAtacado+%C3%93ptico+Afonso+Pena+726+Belo+Horizonte!3m6!1s0xa699fb51549755:0x6b3bd110d92b2e0a!8m2!3d-19.9197793!4d-43.9386492";

function Estrelas({ nota, size = 16 }: { nota: number; size?: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${nota} de 5 estrelas`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          fill={i <= nota ? "#FBBC04" : "#e5e7eb"}
          color={i <= nota ? "#FBBC04" : "#e5e7eb"}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export function GoogleReviews({ titulo = "O que dizem nossos clientes no Google" }: { titulo?: string }) {
  const { data, isLoading, isError } = useQuery<DadosGoogle>({
    queryKey: ["google-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("google-reviews");
      if (error) throw error;
      return data as DadosGoogle;
    },
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

  // Schema.org AggregateRating + Review com dados reais do Google (SEO/GEO).
  useEffect(() => {
    if (!data || data.nota === null || !data.avaliacoes?.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.atacadooptico.com.br/#negocio",
      name: "Atacado Óptico",
      url: "https://www.atacadooptico.com.br",
      telephone: "+553125383333",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Av. Afonso Pena, 726, Sala 800, Centro",
        addressLocality: "Belo Horizonte",
        addressRegion: "MG",
        postalCode: "30130-902",
        addressCountry: "BR",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: data.nota,
        reviewCount: data.total ?? data.avaliacoes.length,
        bestRating: 5,
        worstRating: 1,
      },
      review: data.avaliacoes.slice(0, 6).map((av) => ({
        "@type": "Review",
        author: { "@type": "Person", name: av.autor },
        reviewRating: { "@type": "Rating", ratingValue: av.nota, bestRating: 5 },
        reviewBody: av.texto,
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "schema-google-reviews";
    el.textContent = JSON.stringify(schema);
    document.getElementById("schema-google-reviews")?.remove();
    document.head.appendChild(el);
    return () => { document.getElementById("schema-google-reviews")?.remove(); };
  }, [data]);

  if (isLoading) {
    return (
      <section className="py-14 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-muted-foreground text-sm animate-pulse">
            Carregando avaliações do Google...
          </p>
        </div>
      </section>
    );
  }

  if (isError || !data || !data.avaliacoes || data.avaliacoes.length === 0) {
    return null;
  }

  return (
    <section className="py-14 px-4 bg-white border-b border-gray-100">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-10">
          <div className="text-center sm:text-left">
            <p className="text-xs font-bold uppercase tracking-widest text-[#F07230] mb-1">
              Avaliações verificadas
            </p>
            <h2 className="text-2xl font-black text-[#1A3870]">{titulo}</h2>
          </div>

          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-2xl border border-gray-200 px-5 py-3 hover:shadow-md transition-shadow bg-white shrink-0"
          >
            <GoogleIcon />
            <div className="text-center">
              {data.nota !== null && (
                <>
                  <p className="text-3xl font-black text-[#1A3870] leading-none">
                    {data.nota.toFixed(1)}
                  </p>
                  <Estrelas nota={Math.round(data.nota)} size={14} />
                </>
              )}
              {data.total !== null && (
                <p className="text-xs text-muted-foreground mt-1">
                  {data.total.toLocaleString("pt-BR")} avaliações
                </p>
              )}
            </div>
            <ExternalLink size={14} className="text-muted-foreground" aria-hidden="true" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.avaliacoes.map((av, index) => (
            <div
              key={index}
              className="rounded-xl border border-gray-100 bg-gray-50 p-5 flex flex-col gap-3"
            >
              <div className="flex items-center gap-3">
                {av.foto ? (
                  <img
                    src={av.foto}
                    alt={`Foto de ${av.autor}`}
                    className="w-9 h-9 rounded-full object-cover shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#1A3870] flex items-center justify-center shrink-0">
                    <span className="text-white text-sm font-bold">
                      {av.autor.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A3870] truncate">{av.autor}</p>
                  <p className="text-xs text-muted-foreground">{av.tempo}</p>
                </div>
                <div className="ml-auto shrink-0">
                  <GoogleIcon />
                </div>
              </div>

              <Estrelas nota={av.nota} size={14} />

              <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{av.texto}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#1A3870] px-6 py-3 text-sm font-bold text-[#1A3870] hover:bg-[#1A3870] hover:text-white transition-colors"
          >
            <GoogleIcon />
            Ver todas as avaliações no Google
            <ExternalLink size={14} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}
